import { ChangeEvent, FocusEvent, useEffect } from 'react';
import { memo, useRef } from 'react';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { KioskFormNumericFieldProps } from '../FormNumericField.types';
import { NumericPatternMask } from '../NumericPatternMask/NumericPatternMask';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormNumericField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        keyboardLayout,
        shiftAfterSpace,
        shiftAfterPeriod,
        shiftAfterAmpersand,
        hideKeyboardOnClickOut,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
    }: KioskFormNumericFieldProps) => {
        /**
         * Not all formik validation accounts for TextFields that are null.
         * So even though we pass an undefined validation type to the function,
         * it is still required in order to have null required fields validate.
         */
        const [field, meta] = useField<number | null>({
            name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: undefined,
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The focus ref to prevent a stale state when the on-screen keyboard
         * calls any functions that need to know if the field is focused or not.
         */
        const isFocused = useRef<boolean>(false);
        /**
         * The caret position ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal caret position state.
         */
        const caretPositionRef = useRef<number | null>(null);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         */
        const valueRef = useRef<number | null>(field.value ?? null);
        /**
         * The on-screen keyboard input ref is used to prevent a stale state when we call to update it
         * from keyboard context functions. It is used to prevent the onChange function that recieves a
         * callback from the pattern mask from resetting the caret position incorrectly after we set it
         * using the keyboard context setValue function.
         */
        const wasOSKInput = useRef<boolean>(false);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            if (field.value !== valueRef.current) {
                valueRef.current = field.value;
            }
        }, [field.value]);

        useEffect(() => {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             */
            if (inputRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }

            /**
             * This should run after the onChange function is triggered by the pattern mask onAccept
             * which prevents the caret position from updating the the internal pattern mask position
             * incorrectly.
             */
            if (wasOSKInput.current) {
                wasOSKInput.current = false;
            }
        }, [inputRef.current?.value]);

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const getValue = (): GetValueReturn => {
            /**
             * Converts the formik number typed value to a string so that the on-screen
             * keyboard can parse it.
             */
            const stringifyValue: string = valueRef.current !== null ? valueRef.current.toString() : '';
            /**
             * We initialize the wasOSKInput ref here to be true so that onChange doesn't
             * set a new caret position when it runs.
             */
            wasOSKInput.current = true;

            if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
                const selectionStart: number = inputRef.current.selectionStart;
                const selectionEnd: number = inputRef.current.selectionEnd;

                return {
                    inputValue: stringifyValue,
                    caretPositionStart: selectionStart,
                    caretPositionEnd: selectionEnd,
                };
            }
            else {
                return {
                    inputValue: stringifyValue,
                    caretPositionStart: stringifyValue.length,
                    caretPositionEnd: stringifyValue.length,
                };
            }
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const setValue = ({ value, caretPosition }: SetValueProps): void => {
            caretPositionRef.current = caretPosition;
            /**
             * We convert the string value from the on-screen keyboard
             * into an integer value. The pattern mask prevents any non-numeric
             * input so we know the value will be a number of some kind. If
             * the value is null or empty, we set it to null.
             */
            const valueToNumber: number | null = value === '' ? null : +value;

            valueRef.current = valueToNumber;
            setFieldValue(name, valueToNumber);
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onInputChange = ({ value, caretPosition }: OnInputChangeProps): OnInputChangeReturn => {
            return {
                value: value,
                caretPosition: caretPosition,
            };
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onKeyboardInput = (): void => {
            if (inputRef.current && !isFocused.current) {
                inputRef.current.focus();
            }
        };

        const { onFocus, onBlur } = useKeyboardIntegration({
            fieldValue: valueRef.current ? valueRef.current.toString() : '',
            getValue: getValue,
            setValue: setValue,
            onInputChange: onInputChange,
            onKeyboardInput: onKeyboardInput,
            keyboardLayout: keyboardLayout,
            shiftAfterPeriod: shiftAfterPeriod,
            shiftAfterSpace: shiftAfterSpace,
            shiftAfterAmpersand: shiftAfterAmpersand,
            hideKeyboardOnClickOut: hideKeyboardOnClickOut,
            capsLock: capsLock,
        });

        const handleOnBlur = () => {
            onBlur();
            isFocused.current = false;
        };

        /**
         * This function is called by the pattern mask onAccept function for on-screen keyboard input
         * or called when phsyical keyboard input is entered.
         */
        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            /**
             * Grabs the value from the event object.
             */
            const value: string = event.target.value;
            /**
             * Converts the string to a number since we know it will only be numeric values in the string.
             * If the string is null or empty, we set the number to null.
             */
            const valueToNumber: number | null = value === '' ? null : +value;

            /**
             * We don't want the pattern mask to incorrectly update the caret position,
             * so if the on-screen keyboard was used, this will not trigger.
             */
            if (!wasOSKInput.current) {
                caretPositionRef.current = event.target.selectionEnd;
            }

            valueRef.current = valueToNumber;
            setFieldValue(name, valueToNumber);
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            isFocused.current = true;
            onFocus(event);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={valueRef.current !== null ? valueRef.current.toString() : ''}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                InputProps={{
                    inputComponent: NumericPatternMask as any,
                }}
            />
        );
    },
);
